import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ArrowRight, AlertCircle, Info } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { SmallQuantityEnquiry } from "@/components/SmallQuantityEnquiry";

export default function Cart() {
  const {
    t, cart, extras, eventInfo, selectedPackageId, addToCart, removeFromCart,
    addExtra, decrementExtra, removeExtra, clearCart,
  } = useApp();
  const navigate = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/packages");
        setPkgs(data || []);
      } catch (e) { console.warn(e); }
    })();
  }, []);
  const selectedPackage = useMemo(() => pkgs.find((p) => p.id === selectedPackageId) || null, [pkgs, selectedPackageId]);

  const fetchPricing = useCallback(async () => {
    if (!eventInfo.guests || Number(eventInfo.guests) < 1) { setPricing(null); return; }
    if (cart.length === 0 && extras.length === 0 && !selectedPackageId) { setPricing(null); return; }
    setLoading(true); setErrorMsg(null);
    try {
      const payload = {
        package_id: selectedPackageId || null,
        guests: Number(eventInfo.guests),
        dish_ids: cart.map((c) => c.id),
        extras: extras.map((e) => ({
          dish_id: e.dish_id && !String(e.dish_id).startsWith("cat:") ? e.dish_id : null,
          category_id: e.category_id,
          qty: e.qty,
        })),
      };
      const { data } = await api.post("/pricing/estimate", payload);
      setPricing(data);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not calculate estimate.");
    } finally { setLoading(false); }
  }, [cart, extras, selectedPackageId, eventInfo.guests]);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  const isEmpty = cart.length === 0 && extras.length === 0 && !selectedPackageId;
  const currency = "₹";
  const fmt = (n) => (n === null || n === undefined) ? "—" : `${currency}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="container-x py-16">
      <div className="max-w-2xl">
        <span className="kbd-eyebrow">Your Selection</span>
        <h1 className="mt-4 text-4xl font-semibold text-secondary sm:text-5xl">Your Selection & Price Summary</h1>
      </div>

      {isEmpty ? (
        <div data-testid="cart-empty-state" className="mt-12 rounded-2xl border border-dashed border-border/70 bg-card p-12 text-center">
          <p className="text-sm text-muted-foreground">Your selection is empty. Start from Build Your Menu.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/build" data-testid="cart-empty-build-cta" className="btn-primary">Build Your Menu</Link>
            <Link to="/menu" data-testid="cart-empty-menu-cta" className="btn-ghost">Browse Menu</Link>
          </div>
        </div>
      ) : (
        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_400px]">
          <div className="space-y-8">
            {/* Package block */}
            {selectedPackage && (
              <section data-testid="cart-package-block" className="surface-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-accent">{selectedPackage.tier} package</div>
                    <div className="mt-1 font-serif-display text-xl font-semibold text-secondary">{selectedPackage.name}</div>
                  </div>
                  <Link to="/build" data-testid="cart-edit-package" className="text-xs font-semibold uppercase tracking-[0.14em] text-primary hover:underline">Edit</Link>
                </div>
              </section>
            )}

            {/* Dishes */}
            {cart.length > 0 && (
              <section data-testid="cart-dishes-block" className="surface-card">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif-display text-lg font-semibold text-secondary">Selected dishes ({cart.length})</h2>
                  <Link to="/build" data-testid="cart-edit-dishes" className="text-xs font-semibold uppercase tracking-[0.14em] text-primary hover:underline">Edit</Link>
                </div>
                <ul className="mt-4 space-y-2">
                  {cart.map((c) => (
                    <li key={c.id} data-testid={`cart-item-${c.id}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-background px-3 py-2 text-sm">
                      <span className="truncate font-medium text-secondary">{c.name}</span>
                      <button data-testid={`cart-remove-${c.id}`} onClick={() => removeFromCart(c.id)}
                              className="grid h-8 w-8 place-items-center rounded-full text-destructive hover:bg-destructive/10" aria-label="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Extras */}
            {extras.length > 0 && (
              <section data-testid="cart-extras-block" className="surface-card">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif-display text-lg font-semibold text-secondary">Extra items (per head)</h2>
                  <Link to="/build" data-testid="cart-edit-extras" className="text-xs font-semibold uppercase tracking-[0.14em] text-primary hover:underline">Edit</Link>
                </div>
                <ul className="mt-4 space-y-2">
                  {extras.map((e) => (
                    <li key={e.dish_id} data-testid={`cart-extra-${e.dish_id}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-background px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-secondary">{e.dish_name}</div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">{e.category_name}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1 rounded-full border border-border/70 p-0.5">
                          <button data-testid={`cart-extra-decrement-${e.dish_id}`} onClick={() => decrementExtra(e.dish_id)}
                                  className="grid h-7 w-7 place-items-center rounded-full hover:bg-muted"><Minus className="h-3.5 w-3.5" /></button>
                          <span className="min-w-5 text-center text-xs font-semibold" data-testid={`cart-extra-qty-${e.dish_id}`}>{e.qty}</span>
                          <button data-testid={`cart-extra-increment-${e.dish_id}`} onClick={() => addExtra({ id: e.dish_id, name: e.dish_name, image_id: e.image_id }, { id: e.category_id, name: e.category_name })}
                                  className="grid h-7 w-7 place-items-center rounded-full text-primary hover:bg-primary/10"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <button data-testid={`cart-extra-remove-${e.dish_id}`} onClick={() => removeExtra(e.dish_id)}
                                className="grid h-8 w-8 place-items-center rounded-full text-destructive hover:bg-destructive/10" aria-label="Remove">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="flex justify-end">
              <button data-testid="cart-clear-all" onClick={() => { clearCart(); }} className="text-xs font-semibold uppercase tracking-[0.16em] text-destructive hover:underline">Clear all</button>
            </div>
          </div>

          {/* Price Summary */}
          <aside data-testid="cart-price-summary" className="surface-card h-fit sticky top-24">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Price summary</div>
            <div className="mt-3 space-y-1 text-sm">
              <SummaryRow label="Event"><span className="font-medium text-secondary" data-testid="cart-event-value">{eventInfo.type ? t(`event.types.${eventInfo.type}`) : "—"}</span></SummaryRow>
              <SummaryRow label="Guests"><span className="font-medium text-secondary" data-testid="cart-guest-value">{eventInfo.guests || "—"}</span></SummaryRow>
            </div>

            {loading && <div className="mt-4 rounded-xl bg-muted/60 p-3 text-xs text-muted-foreground">Calculating…</div>}
            {errorMsg && <div className="mt-4 flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5 shrink-0" /><span>{errorMsg}</span></div>}

            {pricing && (
              <>
                {/* Package line */}
                {pricing.package && (
                  <div className="mt-4 border-t border-border/60 pt-3" data-testid="summary-package-line">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Package</div>
                    <div className="mt-1 flex items-baseline justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-secondary">{pricing.package.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {pricing.package.rate === null
                            ? <span className="text-primary">TO BE CONFIGURED</span>
                            : `${fmt(pricing.package.rate)} × ${pricing.package.guests} ${pricing.package.pricing_unit}`}
                        </div>
                      </div>
                      <div className="text-right text-sm font-semibold text-secondary" data-testid="summary-package-amount">
                        {pricing.package.needs_config ? <span className="text-primary">—</span> : fmt(pricing.package.amount)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Extras */}
                {pricing.extras.length > 0 && (
                  <div className="mt-4 border-t border-border/60 pt-3" data-testid="summary-extras-block">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Extras</div>
                    <ul className="mt-2 space-y-2">
                      {pricing.extras.map((l, i) => (
                        <li key={i} data-testid={`summary-extra-${l.dish_id || l.category_id}-${i}`} className="flex items-baseline justify-between gap-2 text-xs">
                          <div className="min-w-0">
                            <div className="truncate font-medium text-secondary">{l.dish_name || l.category_name}</div>
                            <div className="text-muted-foreground">
                              {l.rate === null
                                ? <span className="text-primary font-semibold">TO BE CONFIGURED</span>
                                : `${fmt(l.rate)} × ${l.qty} × ${l.guests} guests`}
                            </div>
                          </div>
                          <span className="shrink-0 font-semibold text-secondary" data-testid={`summary-extra-amount-${i}`}>
                            {l.needs_config ? <span className="text-primary">—</span> : fmt(l.amount)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Subtotal */}
                <div className="mt-4 flex items-baseline justify-between border-t border-border/60 pt-3" data-testid="summary-subtotal">
                  <div className="text-sm font-semibold uppercase tracking-[0.16em] text-secondary">Subtotal</div>
                  <div className="text-xl font-bold text-secondary" data-testid="summary-subtotal-amount">
                    {pricing.subtotal === null ? <span className="text-primary text-base">Partial · {fmt(pricing.subtotal_partial)}</span> : fmt(pricing.subtotal)}
                  </div>
                </div>

                {/* Warnings + needs-config */}
                {(pricing.needs_config?.length > 0 || pricing.warnings?.length > 0) && (
                  <div className="mt-3 space-y-2">
                    {pricing.needs_config.map((n, i) => (
                      <div key={`nc-${i}`} data-testid={`summary-needs-config-${i}`}
                           className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-secondary">
                        <Info className="h-3.5 w-3.5 shrink-0 text-primary" /> {n}
                      </div>
                    ))}
                    {pricing.warnings.map((w, i) => (
                      <div key={`w-${i}`} data-testid={`summary-warning-${i}`}
                           className="flex items-start gap-2 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-[11px] text-secondary">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-accent" /> {w}
                      </div>
                    ))}
                  </div>
                )}

                <p className="mt-4 rounded-xl bg-muted/60 p-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="cart-price-note">
                  {pricing.note}
                </p>
              </>
            )}

            <Link to="/quote" data-testid="cart-request-quote-link" className="btn-primary mt-5 w-full">
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/terms" data-testid="cart-view-terms" className="mt-2 block text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-primary hover:underline">View Terms & Conditions</Link>
          </aside>
        </div>
      )}

      <SmallQuantityEnquiry />
    </div>
  );
}

function SummaryRow({ label, children }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}
